import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  PAGE_SIZES,
} from 'src/shared/constants/table-settings-constants'
import { Dispatch, useCallback, useMemo } from 'react'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { Chip, IconButton } from '@mui/material'
import { DeleteIcon, EditIcon } from 'src/shared/icons/Icons'
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { ConfigurationGridToolbar } from 'src/shared/grid/ConfigurationGridToolbar'
import {
  DataGridDisabledStyled,
  DataGridInactiveRowCssClassId,
} from 'src/shared/styled/data-grid/DataGridDisabledStyled'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'

import { getTranslationForStatus } from 'src/screens/administration/configuration-management/ConfigurationUtils'

interface FeatureGroupsConfigGridProps {
  dataSettings: ConfigurationGridDataSettings
  tableSettings: ConfigurationTablePageSortSettings
  setTableSettings: Dispatch<ConfigurationTablePageSortSettings>
  setFeatureGroupToBeDeleted: Dispatch<number>
}

export const FeatureGroupsConfigGrid = ({
  dataSettings,
  tableSettings,
  setTableSettings,
  setFeatureGroupToBeDeleted,
}: FeatureGroupsConfigGridProps) => {
  const { rows, rowsCount, gridLoading } = dataSettings
  const { paginationModel } = tableSettings
  const { getMessage } = useMessageSource()
  const gridTranslations = useGridTranslateHook()
  const navigate = useDelayedNavigate()

  const handleRowEditClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, params: GridRenderCellParams) => {
      e.stopPropagation()

      const featureGroupId = params.row.id
      const route = ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatureGroupsEdit.params({
        featureGroupId,
      })
      navigate(route)
    },
    [navigate],
  )

  const handleRowDeleteClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, params: GridRenderCellParams) => {
      e.stopPropagation()

      const featureGroupId = params.row.id
      setFeatureGroupToBeDeleted(featureGroupId)
    },
    [setFeatureGroupToBeDeleted],
  )

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: getMessage('label.configuration.features.feature.group.name'),
        flex: 1.5,
        sortable: false,
        filterable: false,
      },
      {
        field: 'feature_type',
        headerName: getMessage('label.feature.type'),
        flex: 0.8,
        sortable: false,
        filterable: false,
      },
      {
        field: 'status',
        headerName: getMessage('label.status'),
        flex: 0.25,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const statusKey = params.value ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE
          return <Chip label={getTranslationForStatus(getMessage, statusKey)} />
        },
      },
      {
        field: 'actions',
        headerName: getMessage('label.configuration.grid.actions'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.25,
        resizable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <>
              {params.row.is_deletable && (
                <span>
                  <IconButton onClick={(e) => handleRowDeleteClick(e, params)} color="primary">
                    <DeleteIcon />
                  </IconButton>
                </span>
              )}
              <span>
                <IconButton onClick={(e) => handleRowEditClick(e, params)} color="primary">
                  <EditIcon />
                </IconButton>
              </span>
            </>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, handleRowEditClick, handleRowDeleteClick])

  const handleAddFeatureGroupClick = useCallback(() => {
    navigate(ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatureGroupsCreate.params({}))
  }, [navigate])

  return (
    <DataGridContainer>
      <DataGridPro
        rows={rows}
        rowCount={rowsCount}
        columns={columns}
        loading={gridLoading}
        localeText={gridTranslations}
        getRowClassName={(params) => (params.row.status ? '' : DataGridInactiveRowCssClassId)}
        disableColumnPinning
        disableRowSelectionOnClick
        disableColumnMenu
        filterMode="server"
        paginationMode="server"
        sortingMode="server"
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={(newPagination) => {
          setTableSettings({
            ...tableSettings,
            paginationModel: newPagination,
          })
        }}
        pageSizeOptions={PAGE_SIZES}
        sx={{
          boxShadow: 'none',
          border: 'none',
          ...DataGridDisabledStyled,
        }}
        slots={{
          toolbar: () => (
            <ConfigurationGridToolbar
              numResults={rowsCount}
              addButtonMessageKey="label.configuration.features.feature.group.grid.toolbar.create.button"
              onAddButtonClick={handleAddFeatureGroupClick}
            />
          ),
        }}
      />
    </DataGridContainer>
  )
}
